import { videos } from "@/mocks/videos";
import { categoryGroups, getAllSubCategories, getSubCategory } from "@/mocks/categories";
import type { Video, VideoType } from "@/types/video";

export const typeLabels: Record<VideoType, string> = {
  movie: "电影",
  series: "电视剧",
  short: "短视频",
};

export type SortKey = "latest" | "hot" | "rating";

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最近更新" },
  { key: "hot", label: "热门度" },
  { key: "rating", label: "评分最高" },
];

export const regions: string[] = Array.from(new Set(videos.map((item) => item.region)));

export const years: number[] = Array.from(new Set(videos.map((item) => item.year))).sort(
  (a, b) => b - a,
);

export const getVideoById = (id: string): Video | undefined => videos.find((item) => item.id === id);

export const getVideosByType = (type: string): Video[] => {
  const group = categoryGroups.find((item) => item.type === type || item.slug === type);
  if (!group) return [];
  return videos.filter((item) => item.type === group.type);
};

export const getVideosByCategoryId = (categoryId: string): Video[] => {
  const sub = getSubCategory(categoryId);
  if (sub) return videos.filter((item) => item.categoryId === categoryId);
  const group = categoryGroups.find((item) => item.type === categoryId || item.slug === categoryId);
  if (group) return videos.filter((item) => item.type === group.type);
  return [];
};

export interface VideoFilter {
  genre?: string;
  region?: string;
  year?: string;
  sort?: SortKey;
}

export const filterVideos = (list: Video[], filter: VideoFilter): Video[] => {
  const result = list.filter((item) => {
    if (filter.genre && filter.genre !== "全部" && !item.genres.includes(filter.genre)) return false;
    if (filter.region && filter.region !== "全部" && item.region !== filter.region) return false;
    if (filter.year && filter.year !== "全部" && String(item.year) !== filter.year) return false;
    return true;
  });

  const sorted = [...result];
  if (filter.sort === "hot") sorted.sort((a, b) => b.views - a.views);
  else if (filter.sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
  else sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  return sorted;
};

export const getGenreOptions = (list: Video[]): string[] =>
  Array.from(new Set(list.flatMap((item) => item.genres)));

export const getRegionsOf = (list: Video[]): string[] =>
  Array.from(new Set(list.map((item) => item.region)));

export const getYearsOf = (list: Video[]): number[] =>
  Array.from(new Set(list.map((item) => item.year))).sort((a, b) => b - a);

export const formatViews = (views: number): string => {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
};

export const searchVideos = (keyword: string): Video[] => {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return videos.filter((item) => {
    const haystack = [
      item.title,
      item.summary,
      item.director,
      item.region,
      item.genres.join(" "),
      item.cast.join(" "),
      typeLabels[item.type],
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  });
};

export const getRelatedVideos = (video: Video, limit = 6): Video[] => {
  const sameDirector = videos.filter(
    (item) => item.id !== video.id && item.director === video.director,
  );
  const sameCategory = videos.filter(
    (item) => item.id !== video.id && item.categoryId === video.categoryId,
  );
  const sameType = videos.filter((item) => item.id !== video.id && item.type === video.type);

  const merged: Video[] = [];
  [...sameDirector, ...sameCategory, ...sameType].forEach((item) => {
    if (merged.length < limit && !merged.some((exist) => exist.id === item.id)) {
      merged.push(item);
    }
  });
  return merged.slice(0, limit);
};

export const getLatestVideos = (limit = 8): Video[] =>
  [...videos].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, limit);

export const getHotVideos = (limit = 8): Video[] =>
  [...videos].sort((a, b) => b.views - a.views).slice(0, limit);

export const getEditorPicks = (limit = 8): Video[] =>
  [...videos].sort((a, b) => b.rating - a.rating).slice(0, limit);

export const getSubcategoryVideoCount = (categoryId: string): number =>
  videos.filter((item) => item.categoryId === categoryId).length;

export const getCategoryName = (categoryId: string): string => {
  const sub = getSubCategory(categoryId);
  if (sub) return sub.name;
  const group = categoryGroups.find(
    (item) => item.type === categoryId || item.slug === categoryId,
  );
  return group ? group.name : categoryId;
};

export const getCategoryType = (categoryId: string): VideoType | undefined => {
  const sub = getSubCategory(categoryId);
  if (sub) return sub.type;
  return categoryGroups.find((item) => item.type === categoryId || item.slug === categoryId)?.type;
};

export const getSubcategoriesOfType = (type: VideoType) =>
  getAllSubCategories().filter((item) => item.type === type);